export const LOCAL_LIVE_STATUSES = [
  "draft",
  "published",
  "live",
  "paused",
  "completed",
  "ended",
  "archived",
];

export const LOCAL_LIVE_STOP_STATUSES = [
  "open",
  "opening_later",
  "closed",
  "sold_out",
];

export const LOCAL_LIVE_INVENTORY_STATUSES = [
  "available",
  "low",
  "sold_out",
  "hidden",
];

export const LOCAL_LIVE_UPDATE_TYPES = [
  "announcement",
  "restock",
  "sold_out",
  "arrival",
  "parking",
  "schedule",
  "general",
];

export const LOCAL_LIVE_LIMITS = Object.freeze({
  maxStops: 50,
  maxVendors: 100,
  maxInventoryItems: 3000,
  maxParticipantInventoryItems: 25,
  maxAdminParticipantInventoryItems: 50,
  maxTextLength: 500,
  maxDescriptionLength: 4000,
  maxPhotosPerStop: 2,
  maxImageBytes: 2 * 1024 * 1024,
});

export const LOCAL_LIVE_MAX_DIRECTIONS_LOCATIONS = 27;
export const FALL_CRAWL_HOMEPAGE_PUBLIC_STATUSES = ["published", "live", "paused", "completed"];
export const FALL_CRAWL_CANONICAL_PUBLIC_STATUSES = [
  "published",
  "live",
  "paused",
  "completed",
  "ended",
  "archived",
];
export const FALL_CRAWL_EVENT_TIME_ZONE = "America/Chicago";
export const FALL_CRAWL_2026_DATE = "2026-09-26";
export const FALL_CRAWL_2026_START_TIME = "08:00";

export const isPublicLocalLiveEvent = (event = {}) =>
  event.visibility === "public" &&
  ["published", "live", "paused", "completed", "ended"].includes(event.status);

export const isPublicFallCrawlHomepageEvent = (event = {}) =>
  event.visibility === "public" &&
  FALL_CRAWL_HOMEPAGE_PUBLIC_STATUSES.includes(event.status);

export const isPublicCanonicalFallCrawlEvent = (event = {}) =>
  event.visibility === "public" &&
  FALL_CRAWL_CANONICAL_PUBLIC_STATUSES.includes(event.status);

export const getFallCrawlHomepageEligibilityMessage = (event = null) => {
  if (!event?.id) return "Select a Local Live event before making the Fall Crawl homepage live.";
  if (event.visibility !== "public") {
    return "This event is not public yet. Set Visibility to Public and save before making it the homepage.";
  }
  if (!FALL_CRAWL_HOMEPAGE_PUBLIC_STATUSES.includes(event.status)) {
    return "This event status is not eligible for the public homepage. Set status to Published, Live, Paused, or Completed first.";
  }
  return "";
};

export const getFallCrawlHomepageAdminStatus = (config = {}, configuredEvent = null) => {
  const configured =
    config.homepageMode === "fall-crawl" &&
    Boolean(config.fallCrawlEventId);

  if (!configured) {
    return {
      state: "default",
      label: "NORMAL MFP HOMEPAGE LIVE",
      detail: "Homepage takeover is off.",
      isPubliclyLive: false,
    };
  }

  if (isPublicFallCrawlHomepageEvent(configuredEvent || {})) {
    return {
      state: "fall-crawl-live",
      label: "FALL CRAWL HOMEPAGE LIVE",
      detail: `${configuredEvent.title || "Configured Local Live event"} is eligible and rendering on the public homepage.`,
      isPubliclyLive: true,
    };
  }

  return {
    state: "fall-crawl-inactive",
    label: "TAKEOVER CONFIGURED - NORMAL MFP HOMEPAGE LIVE",
    detail: "The configured Local Live event is missing, private, draft, ended, archived, or otherwise not eligible, so public visitors see the normal MFP homepage.",
    isPubliclyLive: false,
  };
};

const parseLocalLiveDateParts = (dateValue, timeValue = "00:00") => {
  if (!dateValue) return null;
  const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return null;
  const [hourValue = "0", minuteValue = "0", secondValue = "0"] = String(timeValue || "00:00").split(":");
  const parts = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(hourValue),
    minute: Number(minuteValue),
    second: Number(secondValue),
  };
  return Object.values(parts).every(Number.isFinite) ? parts : null;
};

const normalizeLocalLiveTimeZone = (timeZone) => {
  const candidate = String(timeZone || FALL_CRAWL_EVENT_TIME_ZONE).trim();
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate });
    return candidate;
  } catch {
    return FALL_CRAWL_EVENT_TIME_ZONE;
  }
};

const getTimeZoneOffsetMs = (date, timeZone = FALL_CRAWL_EVENT_TIME_ZONE) => {
  const safeTimeZone = normalizeLocalLiveTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: safeTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).reduce((values, part) => {
    if (part.type !== "literal") values[part.type] = Number(part.value);
    return values;
  }, {});

  const zonedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return zonedAsUtc - date.getTime();
};

export const getLocalLiveZonedDateTime = (
  dateValue,
  timeValue = "00:00",
  timeZone = FALL_CRAWL_EVENT_TIME_ZONE
) => {
  const parts = parseLocalLiveDateParts(dateValue, timeValue);
  if (!parts) return null;

  const initialUtc = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  ));
  const firstOffset = getTimeZoneOffsetMs(initialUtc, timeZone);
  const resolved = new Date(initialUtc.getTime() - firstOffset);
  const secondOffset = getTimeZoneOffsetMs(resolved, timeZone);
  return new Date(initialUtc.getTime() - secondOffset);
};

export const getFallCrawlEventTimeline = (event = {}, now = new Date()) => {
  const current = now instanceof Date ? now : new Date(now);
  const effectiveEvent = getFallCrawlScheduleEvent(event);
  const startAt = getLocalLiveZonedDateTime(effectiveEvent.startDate, effectiveEvent.startTime || "00:00");
  const endAt = getLocalLiveZonedDateTime(
    effectiveEvent.endDate || effectiveEvent.startDate,
    effectiveEvent.endTime || "23:59"
  );

  if (event.status === "archived" || event.status === "ended" || event.status === "completed") {
    return { phase: "completed", startAt, endAt, msToStart: 0, msToEnd: 0 };
  }
  if (event.status === "paused") {
    return { phase: "paused", startAt, endAt, msToStart: 0, msToEnd: 0 };
  }
  if (!startAt || !endAt || Number.isNaN(current.getTime())) {
    return { phase: "unavailable", startAt, endAt, msToStart: 0, msToEnd: 0 };
  }

  const msToStart = startAt.getTime() - current.getTime();
  const msToEnd = endAt.getTime() - current.getTime();
  if (msToStart > 0) return { phase: "upcoming", startAt, endAt, msToStart, msToEnd };
  if (msToEnd > 0) return { phase: "live", startAt, endAt, msToStart: 0, msToEnd };
  return { phase: "completed", startAt, endAt, msToStart: 0, msToEnd: 0 };
};

const hasFallCrawl2026Identity = (event = {}) => {
  const values = [
    event.passportEventKey,
    event.slug,
    event.id,
    event.title,
  ].map((value) => String(value || "").toLowerCase());

  return event.fallCrawl2026 === true ||
    values.some((value) => value.includes("fall-crawl-2026"));
};

export const getFallCrawlScheduleEvent = (event = {}, stops = []) => {
  if (!hasFallCrawl2026Identity(event)) return event || {};

  const latestCloseTime = getLatestLocalLiveStopCloseTime(stops) || event.endTime || "";
  return {
    ...(event || {}),
    startDate: FALL_CRAWL_2026_DATE,
    startTime: FALL_CRAWL_2026_START_TIME,
    endDate: FALL_CRAWL_2026_DATE,
    endTime: latestCloseTime || event.endTime || "23:59",
  };
};

export const getFallCrawlCountdownParts = (milliseconds = 0) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

const getEventBoundary = (dateValue, timeValue, fallbackTime, timeZone) => {
  const date = getLocalLiveZonedDateTime(dateValue, timeValue || fallbackTime, timeZone);
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

export const getLocalLiveEventPhase = (event = {}, now = new Date()) => {
  if (event.status === "archived") return "ended";
  if (event.status === "completed") return "ended";
  if (event.status === "paused") return "paused";
  if (event.status === "live") return "live";
  if (event.status === "ended") return "ended";

  const current = now instanceof Date ? now : new Date(now);
  const timeZone = event.timezone || FALL_CRAWL_EVENT_TIME_ZONE;
  const start = getEventBoundary(event.startDate, event.startTime, "00:00:00", timeZone);
  const end = getEventBoundary(event.endDate || event.startDate, event.endTime, "23:59:59", timeZone);

  if (end && current > end) return "ended";
  if (start && current < start) return "upcoming";
  if (start || end) return "live";
  return event.status === "published" ? "upcoming" : "upcoming";
};

export const getLocalLiveEventStatusLabel = (event = {}) => {
  if (event.status === "completed") return "Completed";
  const phase = getLocalLiveEventPhase(event);
  if (phase === "live") return "LIVE";
  if (phase === "paused") return "Paused";
  if (phase === "ended") return "Ended";
  return "Upcoming";
};

export const getLocalLiveStatusMessage = (event = {}) => {
  if (event.status === "completed") {
    return "The Fall Crawl 2026 has wrapped, but we’re leaving the route up for a few days so you can revisit participating stops, find places you missed, and keep supporting local.";
  }
  const phase = getLocalLiveEventPhase(event);
  if (phase === "live") return `${event.title || "This Local Live event"} is happening now.`;
  if (phase === "ended") return "Thanks for joining us.";
  if (phase === "paused") return "This event is paused. Check the latest updates before heading out.";
  return event.startDate
    ? `${event.title || "This Local Live event"} begins ${event.startDate}.`
    : "Event details are coming soon.";
};

export const getLocalLiveStopStatusLabel = (status) => {
  const labels = {
    open: "Open",
    opening_later: "Opening later",
    closing_soon: "Closing soon",
    closed: "Closed",
    sold_out: "Sold out",
  };

  return labels[status] || "Status unavailable";
};

export const formatLocalLiveTime = (value = "") => {
  if (!value) return "";
  const [hourValue, minuteValue = "00"] = String(value).split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return String(value);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const formatLocalLiveDateLabel = (value = "") => {
  if (!value) return "";
  const parts = parseLocalLiveDateParts(value);
  if (!parts) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
};

const LOCAL_LIVE_WEEKDAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const timeToMinutes = (value = "") => {
  const [hourValue, minuteValue = "0"] = String(value || "").split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const getLocalLiveDateValue = (date, timeZone = FALL_CRAWL_EVENT_TIME_ZONE) => {
  const current = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(current.getTime())) return "";
  const safeTimeZone = normalizeLocalLiveTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: safeTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(current).reduce((values, part) => {
    if (part.type !== "literal") values[part.type] = part.value;
    return values;
  }, {});

  return parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : "";
};

const getLocalLiveWeekdayValue = (date, timeZone = FALL_CRAWL_EVENT_TIME_ZONE) => {
  const current = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(current.getTime())) return "";
  const safeTimeZone = normalizeLocalLiveTimeZone(timeZone);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: safeTimeZone,
    weekday: "long",
  }).format(current).toLowerCase();
};

export const normalizeLocalLiveDisplaySchedule = (schedule = {}) => {
  if (!schedule || typeof schedule !== "object") return null;
  const mode = String(schedule.mode || "").trim();
  const startTime = String(schedule.startTime || "").trim();
  const endTime = String(schedule.endTime || "").trim();
  const startDate = String(schedule.startDate || "").trim();
  const endDate = String(schedule.endDate || "").trim();
  const selectedDates = Array.isArray(schedule.selectedDates)
    ? [...new Set(schedule.selectedDates.map((date) => String(date || "").trim()).filter(Boolean))]
    : [];
  const weekdays = Array.isArray(schedule.weekdays)
    ? [...new Set(schedule.weekdays.map((day) => String(day || "").trim().toLowerCase()).filter(Boolean))]
    : [];
  const unavailableDates = Array.isArray(schedule.unavailableDates)
    ? [...new Set(schedule.unavailableDates.map((date) => String(date || "").trim()).filter(Boolean))]
    : [];

  if (!["date_range", "selected_dates", "recurring_weekdays"].includes(mode)) return null;
  if (!startTime || !endTime) return null;
  if (mode === "selected_dates" && selectedDates.length === 0) return null;
  if (mode !== "selected_dates" && (!startDate || !endDate)) return null;
  if (mode === "recurring_weekdays" && weekdays.length === 0) return null;

  return {
    mode,
    startDate,
    endDate,
    selectedDates,
    weekdays,
    startTime,
    endTime,
    unavailableDates,
  };
};

export const isLocalLiveDisplayOperatingOnDate = (
  schedule = {},
  dateValue = ""
) => {
  const normalized = normalizeLocalLiveDisplaySchedule(schedule);
  if (!normalized || !dateValue) return false;
  if (normalized.unavailableDates.includes(dateValue)) return false;
  if (normalized.mode === "selected_dates") {
    return normalized.selectedDates.includes(dateValue);
  }
  if (dateValue < normalized.startDate || dateValue > normalized.endDate) {
    return false;
  }
  if (normalized.mode === "recurring_weekdays") {
    const date = getLocalLiveZonedDateTime(dateValue, "12:00");
    const weekday = getLocalLiveWeekdayValue(date);
    return normalized.weekdays.includes(weekday);
  }
  return true;
};

export const getLocalLiveDisplayScheduleText = (schedule = {}) => {
  const normalized = normalizeLocalLiveDisplaySchedule(schedule);
  if (!normalized) return "";
  const hours = `${formatLocalLiveTime(normalized.startTime)}-${formatLocalLiveTime(normalized.endTime)}`;
  const unavailable = normalized.unavailableDates.length > 0
    ? `; closed ${normalized.unavailableDates.map(formatLocalLiveDateLabel).join(", ")}`
    : "";
  if (normalized.mode === "selected_dates") {
    return `${normalized.selectedDates.map(formatLocalLiveDateLabel).join(", ")}; ${hours}${unavailable}`;
  }
  if (normalized.mode === "recurring_weekdays") {
    const days = normalized.weekdays
      .map((day) => LOCAL_LIVE_WEEKDAY_LABELS[day] || day)
      .join(", ");
    return `${days}, ${formatLocalLiveDateLabel(normalized.startDate)}-${formatLocalLiveDateLabel(normalized.endDate)}; ${hours}${unavailable}`;
  }
  return `Every evening ${formatLocalLiveDateLabel(normalized.startDate)}-${formatLocalLiveDateLabel(normalized.endDate)}; ${hours}${unavailable}`;
};

const hasLocalLiveStopEventHours = (stop = {}) =>
  Boolean(
    stop.eventDayStartTime ||
    stop.openTime ||
    stop.openingTime ||
    stop.eventDayCloseTime ||
    stop.closeTime ||
    stop.closingTime ||
    stop.hours
  );

export const getLatestLocalLiveStopCloseTime = (stops = []) => {
  const latest = stops.reduce((best, stop) => {
    if (!isLocalLiveRouteStop(stop)) return best;
    const minutes = timeToMinutes(stop.eventDayCloseTime || stop.closeTime || stop.closingTime);
    if (minutes == null) return best;
    return !best || minutes > best.minutes
      ? { minutes, value: stop.eventDayCloseTime || stop.closeTime || stop.closingTime }
      : best;
  }, null);

  return latest?.value || "";
};

export const getFallCrawlEventHoursText = (event = {}, stops = []) => {
  if (!hasFallCrawl2026Identity(event)) {
    const start = formatLocalLiveTime(event.startTime);
    const end = formatLocalLiveTime(event.endTime);
    return start && end ? `${start} - ${end}` : start || end || "Time to be announced";
  }

  const start = formatLocalLiveTime(FALL_CRAWL_2026_START_TIME);
  return `${start} - Until the last stop closes`;
};

export const getLocalLiveStopHoursText = (stop = {}, event = {}) => {
  const displayScheduleText = getLocalLiveDisplayScheduleText(stop.displaySchedule);
  if (displayScheduleText) return displayScheduleText;
  const openTime = stop.eventDayStartTime || stop.openTime || stop.openingTime;
  const closeTime = stop.eventDayCloseTime || stop.closeTime || stop.closingTime;
  const formattedOpen = formatLocalLiveTime(openTime);
  const formattedClose = formatLocalLiveTime(closeTime);
  if (formattedOpen && formattedClose) return `${formattedOpen}–${formattedClose}`;
  if (closeTime) return `Open until ${formatLocalLiveTime(closeTime)}`;
  if (openTime) return `Opens at ${formatLocalLiveTime(openTime)}`;
  if (stop.hours) return stop.hours;
  if (hasFallCrawl2026Identity(event)) return "Closing time varies";
  return "";
};

export const getLocalLiveStopStatusDetails = (stop = {}, event = {}, now = new Date()) => {
  if (LOCAL_LIVE_STOP_STATUSES.includes(stop.adminStatusOverride)) {
    return { status: stop.adminStatusOverride, source: "admin_override" };
  }

  const scheduleEvent = getFallCrawlScheduleEvent(event);
  const current = now instanceof Date ? now : new Date(now);
  const timeZone = scheduleEvent.timezone || FALL_CRAWL_EVENT_TIME_ZONE;
  const displaySchedule = normalizeLocalLiveDisplaySchedule(stop.displaySchedule);
  if (displaySchedule) {
    const currentDate = getLocalLiveDateValue(current, timeZone);
    const operatingToday = isLocalLiveDisplayOperatingOnDate(displaySchedule, currentDate);
    if (!operatingToday) return { status: "closed", source: "display_schedule" };
    const startAt = getLocalLiveZonedDateTime(currentDate, displaySchedule.startTime, timeZone);
    const closeAt = getLocalLiveZonedDateTime(currentDate, displaySchedule.endTime, timeZone);
    if (!Number.isNaN(current.getTime())) {
      if (startAt && current < startAt) return { status: "opening_later", source: "display_schedule" };
      if (closeAt && current >= closeAt) return { status: "closed", source: "display_schedule" };
      if (closeAt && closeAt.getTime() - current.getTime() <= 60 * 60 * 1000) {
        return { status: "closing_soon", source: "display_schedule" };
      }
    }
    return { status: "open", source: "display_schedule" };
  }
  const isLiveEventDay =
    scheduleEvent.status === "live" &&
    scheduleEvent.startDate &&
    getLocalLiveDateValue(current, timeZone) === scheduleEvent.startDate;
  const shouldUseLiveDaySchedule =
    isLiveEventDay &&
    (stop.status === "opening_later" || stop.status === "closed");

  if (!shouldUseLiveDaySchedule && (
    stop.status === "opening_later" ||
    stop.status === "closed" ||
    stop.status === "sold_out"
  )) {
    return { status: stop.status, source: "participant" };
  }

  const startAt = getLocalLiveZonedDateTime(
    scheduleEvent.startDate,
    stop.eventDayStartTime || scheduleEvent.startTime || "00:00",
    timeZone
  );
  const closeTime = stop.eventDayCloseTime || stop.closeTime || stop.closingTime || "";
  const closeAt = getLocalLiveZonedDateTime(
    scheduleEvent.endDate || scheduleEvent.startDate,
    closeTime,
    timeZone
  );

  if (!Number.isNaN(current.getTime())) {
    if (startAt && current < startAt) return { status: "opening_later", source: "schedule" };
    if (closeTime && closeAt && current >= closeAt) return { status: "closed", source: "schedule" };
    if (closeTime && closeAt && closeAt.getTime() - current.getTime() <= 60 * 60 * 1000) {
      return { status: "closing_soon", source: "schedule" };
    }
  }

  if (shouldUseLiveDaySchedule && !hasLocalLiveStopEventHours(stop)) {
    return { status: "open", source: "schedule" };
  }
  if (shouldUseLiveDaySchedule && (stop.status === "opening_later" || stop.status === "closed")) {
    return { status: "open", source: "schedule" };
  }

  return {
    status: stop.status || "open",
    source: LOCAL_LIVE_STOP_STATUSES.includes(stop.status) ? "participant" : "schedule",
  };
};

export const getLocalLiveEffectiveStopStatus = (stop = {}, event = {}, now = new Date()) =>
  getLocalLiveStopStatusDetails(stop, event, now).status;

export const getLocalLiveInventoryStatusLabel = (status) => {
  const labels = {
    available: "Available",
    low: "Low stock",
    sold_out: "Sold out",
    hidden: "Hidden",
  };

  return labels[status] || "Availability unavailable";
};

export const getLocalLiveInventoryPriceLabel = (price) => {
  const text = String(price ?? "").trim();
  if (!text) return "";
  return /^\d+(\.\d{1,2})?$/.test(text) ? `$${text}` : text;
};

export const getLocalLiveInventoryItemSummary = (item = {}) => {
  const parts = [
    item.itemName || "Untitled item",
    getLocalLiveInventoryPriceLabel(item.price),
    typeof item.quantity === "number" ? `${item.quantity} left` : "",
  ];
  return parts.filter(Boolean).join(" · ");
};

export const getLocalLiveInventoryItemLabel = (item = {}) => [
  getLocalLiveInventoryItemSummary(item),
  getLocalLiveInventoryStatusLabel(item.availability),
].filter(Boolean).join(" · ");

export const getLocalLiveDirectionsUrl = (stop = {}) => {
  if (Number.isFinite(Number(stop.lat)) && Number.isFinite(Number(stop.lng))) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      `${Number(stop.lat)},${Number(stop.lng)}`
    )}`;
  }

  const destination = [stop.name, stop.address, stop.city, stop.state]
    .filter(Boolean)
    .join(", ");

  if (!destination) return "";

  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}`;
};

export const getLocalLiveTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatLocalLiveUpdatedAt = (value, now = Date.now()) => {
  const date = getLocalLiveTimestamp(value);
  if (!date) return "Update time unavailable";

  const minutes = Math.max(0, Math.floor((now - date.getTime()) / 60000));
  if (minutes < 1) return "Updated just now";
  if (minutes === 1) return "Updated 1 minute ago";
  if (minutes < 60) return `Updated ${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "Updated 1 hour ago";
  return `Updated ${hours} hours ago`;
};

export const normalizeLocalLiveText = (value, maxLength = 500) =>
  String(value || "").trim().slice(0, maxLength);

export const buildLocalLiveStopAddress = (stop = {}) =>
  [stop.address, stop.city, stop.state, stop.zip]
    .map((part) => normalizeLocalLiveText(part, 240))
    .filter(Boolean)
    .join(", ");

export const normalizeLocalLiveSlug = (value, maxLength = 80) => {
  const rawValue = String(value || "").trim();
  let candidate = rawValue;

  try {
    const parsed = new URL(rawValue);
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const localLiveIndex = pathParts.findIndex((part) => part.toLowerCase() === "local-live");
    candidate = pathParts[localLiveIndex + 1] || pathParts[pathParts.length - 1] || "";
  } catch (error) {
    // Plain slugs do not need URL parsing.
  }

  return candidate
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength)
    .replace(/-+$/, "");
};

export const buildLocalLiveEventPayload = (eventForm = {}) => ({
  title: normalizeLocalLiveText(eventForm.title, 160),
  slug: normalizeLocalLiveSlug(eventForm.slug),
  description: normalizeLocalLiveText(eventForm.description, 4000),
  startDate: eventForm.startDate || "",
  startTime: eventForm.startTime || "",
  endDate: eventForm.endDate || "",
  endTime: eventForm.endTime || "",
  timezone: eventForm.timezone || "America/Chicago",
  status: LOCAL_LIVE_STATUSES.includes(eventForm.status) ? eventForm.status : "draft",
  visibility: eventForm.visibility === "public" ? "public" : "private",
  organizerName: normalizeLocalLiveText(eventForm.organizerName, 160),
  bannerUrl: normalizeLocalLiveText(eventForm.bannerUrl, 2000),
  eventNotice: normalizeLocalLiveText(eventForm.eventNotice, 500),
  mapEnabled: eventForm.mapEnabled !== false,
  participantUpdatesEnabled: eventForm.participantUpdatesEnabled !== false,
  liveInventoryEnabled: eventForm.liveInventoryEnabled !== false,
  visitorPresenceEnabled: eventForm.visitorPresenceEnabled === true,
});

export const groupLocalLiveInventory = (items = []) =>
  items.reduce((groups, item) => {
    const key = item.stopId || item.hostStopId || item.participantId || "unassigned";
    groups[key] = groups[key] || [];
    groups[key].push(item);
    return groups;
  }, {});

export const sortLocalLiveStopsByEventOrder = (stops = []) =>
  [...stops].sort((a, b) => {
    const orderDelta = Number(a.order || 0) - Number(b.order || 0);
    if (orderDelta !== 0) return orderDelta;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

export const getNextLocalLiveStopOrder = (stops = []) => {
  const maxOrder = stops.reduce((max, stop) => {
    const order = Number(stop.order || 0);
    return Number.isFinite(order) && order > max ? order : max;
  }, 0);
  return maxOrder + 1;
};

export const reorderLocalLiveStopsByIdOrder = (stops = [], orderedIds = []) => {
  const stopById = new Map(stops.filter((stop) => stop?.id).map((stop) => [stop.id, stop]));
  const seenIds = new Set();
  const orderedStops = [];

  orderedIds.forEach((stopId) => {
    if (!stopById.has(stopId) || seenIds.has(stopId)) return;
    seenIds.add(stopId);
    orderedStops.push(stopById.get(stopId));
  });

  sortLocalLiveStopsByEventOrder(stops).forEach((stop) => {
    if (!stop?.id || seenIds.has(stop.id)) return;
    seenIds.add(stop.id);
    orderedStops.push(stop);
  });

  return orderedStops.map((stop, index) => ({ ...stop, order: index + 1 }));
};

export const isLocalLiveRouteStop = (stop = {}) =>
  stop.publicEnabled !== false && stop.active !== false;

export const getLocalLiveRouteStops = (stops = []) =>
  sortLocalLiveStopsByEventOrder(stops).filter(isLocalLiveRouteStop);

export const getLocalLiveRouteSection = (stop = {}) => {
  const section = Number(stop.routeSection || 1);
  return Number.isInteger(section) && section > 0 ? section : 1;
};

export const getLocalLiveHostedMakersForStop = (vendors = [], stopId = "") =>
  vendors.filter((vendor) => vendor.hostStopId === stopId);

export const resequenceLocalLiveStopsAfterRemoval = (stops = [], removedStopId = "") =>
  sortLocalLiveStopsByEventOrder(stops)
    .filter((stop) => stop.id !== removedStopId)
    .map((stop, index) => ({ ...stop, order: index + 1 }));

export const getLocalLiveStopCoordinates = (stop) => {
  if (stop?.lat === "" || stop?.lng === "" || stop?.lat == null || stop?.lng == null) return null;
  const lat = Number(stop.lat);
  const lng = Number(stop.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const getLocalLiveDistanceMiles = (from, to) => {
  if (!from || !to) return null;
  const fromLat = Number(from.lat);
  const fromLng = Number(from.lng);
  const toLat = Number(to.lat);
  const toLng = Number(to.lng);
  if (![fromLat, fromLng, toLat, toLng].every(Number.isFinite)) return null;

  const radians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const deltaLat = radians(toLat - fromLat);
  const deltaLng = radians(toLng - fromLng);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(radians(fromLat)) *
      Math.cos(radians(toLat)) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatLocalLiveDistance = (distanceMiles) => {
  if (!Number.isFinite(distanceMiles)) return "";
  if (distanceMiles < 0.1) return "less than 0.1 mi";
  if (distanceMiles < 10) return `${distanceMiles.toFixed(1)} mi`;
  return `${Math.round(distanceMiles)} mi`;
};

export const sortLocalLiveStops = (stops = [], visitorLocation = null) =>
  [...stops]
    .map((stop) => {
      const point = getLocalLiveStopCoordinates(stop);
      const distanceMiles = visitorLocation && point
        ? getLocalLiveDistanceMiles(visitorLocation, point)
        : null;
      return { ...stop, distanceMiles };
    })
    .sort((a, b) => {
      const aHasDistance = Number.isFinite(a.distanceMiles);
      const bHasDistance = Number.isFinite(b.distanceMiles);
      if (aHasDistance && bHasDistance) return a.distanceMiles - b.distanceMiles;
      if (aHasDistance) return -1;
      if (bHasDistance) return 1;
      const orderDelta = Number(a.order || 0) - Number(b.order || 0);
      if (orderDelta !== 0) return orderDelta;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

export const addLocalLiveStopDistances = (stops = [], visitorLocation = null) =>
  stops.map((stop) => {
    const point = getLocalLiveStopCoordinates(stop);
    const distanceMiles = visitorLocation && point
      ? getLocalLiveDistanceMiles(visitorLocation, point)
      : null;
    return { ...stop, distanceMiles };
  });

export const getOrderedLocalLiveMapStops = (stops = []) =>
  getLocalLiveRouteStops(stops)
    .map((stop) => ({ stop, point: getLocalLiveStopCoordinates(stop) }))
    .filter((item) => item.point);

export const getLocalLiveRouteKey = (mapStops = []) =>
  mapStops
    .map(({ stop, point }) => [
      stop.id,
      Number(stop.order || 0),
      getLocalLiveRouteSection(stop),
      Number(point.lat).toFixed(6),
      Number(point.lng).toFixed(6),
    ].join(":"))
    .join("|");

export const buildLocalLiveRouteSegments = (
  orderedMappedStops = [],
  maxLocations = LOCAL_LIVE_MAX_DIRECTIONS_LOCATIONS
) => {
  if (orderedMappedStops.length < 2) return [];
  if (orderedMappedStops.length <= maxLocations) return [orderedMappedStops];

  const segments = [];
  let startIndex = 0;
  const step = maxLocations - 1;
  while (startIndex < orderedMappedStops.length - 1) {
    const segment = orderedMappedStops.slice(startIndex, startIndex + maxLocations);
    if (segment.length >= 2) segments.push(segment);
    startIndex += step;
  }
  return segments;
};

export const buildLocalLiveDirectionsRequest = (segment = [], options = {}) => {
  if (segment.length < 2) return null;
  return {
    origin: segment[0].point,
    destination: segment[segment.length - 1].point,
    waypoints: segment.slice(1, -1).map(({ point }) => ({
      location: point,
      stopover: true,
    })),
    optimizeWaypoints: options.optimizeWaypoints === true,
  };
};

export const applyLocalLiveWaypointOrder = (orderedStops = [], waypointOrder = []) => {
  if (orderedStops.length <= 2) {
    return orderedStops.map((stop, index) => ({ ...stop, order: index + 1 }));
  }

  const firstStop = orderedStops[0];
  const lastStop = orderedStops[orderedStops.length - 1];
  const intermediateStops = orderedStops.slice(1, -1);
  const hasValidWaypointOrder =
    waypointOrder.length === intermediateStops.length &&
    new Set(waypointOrder).size === waypointOrder.length &&
    waypointOrder.every((index) => Number.isInteger(index) && index >= 0 && index < intermediateStops.length);
  const optimizedIntermediates = hasValidWaypointOrder
    ? waypointOrder.map((index) => intermediateStops[index])
    : intermediateStops;

  return [firstStop, ...optimizedIntermediates, lastStop]
    .map((stop, index) => ({ ...stop, order: index + 1 }));
};

export const getLocalLiveRouteSections = (stops = []) => {
  const sections = new Map();
  getLocalLiveRouteStops(stops).forEach((stop) => {
    const section = getLocalLiveRouteSection(stop);
    if (!sections.has(section)) sections.set(section, []);
    sections.get(section).push(stop);
  });

  return [...sections.entries()]
    .sort(([sectionA], [sectionB]) => sectionA - sectionB)
    .map(([section, sectionStops]) => ({
      section,
      stops: sortLocalLiveStopsByEventOrder(sectionStops),
    }));
};

export const getLocalLiveOptimizationPlan = (stops = []) => {
  const orderedStops = sortLocalLiveStopsByEventOrder(stops);
  const routeStops = getLocalLiveRouteStops(orderedStops);
  const missingCoordinateStops = routeStops.filter((stop) => !getLocalLiveStopCoordinates(stop));
  const sections = routeStops.length > LOCAL_LIVE_MAX_DIRECTIONS_LOCATIONS
    ? getLocalLiveRouteSections(routeStops)
    : [{ section: 1, stops: routeStops }];
  const oversizedSections = sections.filter(
    ({ stops: sectionStops }) => sectionStops.length > LOCAL_LIVE_MAX_DIRECTIONS_LOCATIONS
  );

  return {
    orderedStops,
    routeStops,
    sections,
    missingCoordinateStops,
    oversizedSections,
  };
};

export const buildLocalLiveOptimizedGlobalOrder = (allStops = [], optimizedSections = []) => {
  const optimizedRouteStops = optimizedSections.flatMap(({ stops: sectionStops }) => sectionStops);
  const optimizedRouteIds = new Set(optimizedRouteStops.map((stop) => stop.id));
  const nonRouteStops = sortLocalLiveStopsByEventOrder(allStops)
    .filter((stop) => !optimizedRouteIds.has(stop.id));

  return [...optimizedRouteStops, ...nonRouteStops]
    .map((stop, index) => ({ ...stop, order: index + 1 }));
};

export const getLocalLiveUpdateTimestamp = (update = {}) =>
  getLocalLiveTimestamp(update.createdAt || update.lastUpdatedAt || update.updatedAt);

export const sortLocalLiveUpdates = (updates = []) =>
  [...updates].sort((a, b) => {
    const aTime = getLocalLiveUpdateTimestamp(a)?.getTime() || 0;
    const bTime = getLocalLiveUpdateTimestamp(b)?.getTime() || 0;
    return bTime - aTime;
  });
